/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { ReferralState } from '../__generated__/internalGlobalTypes';
import { BadgeColor } from '../components/StatusBadge';
import { IGetLead, IGetLeadLead } from '../graphql/queries/GetLead';
import { IOffer, IReferral } from '../graphql/queries/SearchReferrals';
import { centsToCurrencyString } from './formatUtils';
import { WEB_API_URL } from './globals';

type OfferDataLabel =
  | 'Offer Exp.'
  | 'Closing Date'
  | 'Offer Range'
  | 'Offer Price'
  | 'Final Offer'
  | 'Fee'
  | 'Repairs'
  | 'Assessments'
  | 'Exterior Asmt.'
  | 'Interior Asmt.'
  | 'Net Cash (est.)';

export enum State {
  OFFER = 0,
  ASSESSMENT,
  CONTRACT,
  CLOSED,
  COMPLETE,
}

export const STATUS_MAP: { [K in ReferralState]: { color: BadgeColor; text: string } } = {
  UNKNOWN: { color: 'red', text: 'Unknown' },
  LEAD_DENIED: { color: 'red', text: 'Denied' },
  LEAD_QUALIFIED: { color: 'green', text: 'Offer Requested' },
  LEAD_SELLER_FLOW_INCOMPLETE: { color: 'red', text: 'Incomplete Seller Flow' },
  OFFER_ACCEPTED: { color: 'green', text: 'Offer Accepted' },
  OFFER_ASSESSMENTS_COMPLETED: { color: 'green', text: 'Assessments Completed' },
  OFFER_CLOSED: { color: 'green', text: 'Closed' },
  OFFER_CONTINGENCIES_RELEASED: { color: 'green', text: 'Contingencies Released' },
  OFFER_EXPIRED: { color: 'red', text: 'Offer Expired' },
  OFFER_EXPIRING_SOON: { color: 'red', text: 'Offer Expiring Soon' },
  OFFER_IN_CONTRACT: { color: 'green', text: 'Contract Signed' },
  OFFER_SENT: { color: 'green', text: 'Offer Sent' },
  OFFER_VIEWED: { color: 'green', text: 'Offer Viewed' },
  OFFER_WITHDRAWN: { color: 'red', text: 'Withdrawn' },
};

export type Offer = {
  offerData: Array<{
    label: OfferDataLabel;
    value: string;
  }>;
  state: State;
  status: ReferralState;
  url?: string;
  cepName?: string | null;
  token: string | null;
};

export type CustomerCardData = {
  customer: {
    email?: string | null;
    fullName?: string | null;
    phoneNumber?: string | null;
    referralUuid: string;
    street: string;
  };
  offer: Offer;
  qualifiedAt: number;
  hidden: boolean;
  sellerInputId: string;
};

function daysHaveElapsed(dateStr: string, numDays: number) {
  const date = new Date(dateStr).getTime();
  return (new Date().getTime() - date) / (1000 * 60 * 60 * 24) >= numDays;
}

export function daysLeft(dateStr: string, numDays: number) {
  const date = new Date(dateStr).getTime();
  const diff = (new Date().getTime() - date) / (1000 * 60 * 60 * 24);
  return diff < 2 && diff > -1 * numDays;
}

function shouldHide(lead: NonNullable<IReferral['lead']>, state: State): boolean {
  const { offer } = lead;
  switch (state) {
    case State.OFFER:
      if (!lead.qualifiedAt) {
        return true;
      }
      return daysHaveElapsed(lead.qualifiedAt, 14);
    case State.ASSESSMENT:
    case State.CONTRACT:
      if (
        !offer ||
        !(
          offer.acceptanceExpiresAt ||
          offer.assessmentSchedulingExpiresAt ||
          offer.purchaseAgreementExpiresAt
        )
      ) {
        return true;
      }

      // In case any of the above fields are missing for whatever reason
      if (offer.expiredAt && daysHaveElapsed(offer.expiredAt, 90)) {
        return true;
      }

      return false;
    case State.CLOSED:
    case State.COMPLETE:
      if (!offer) {
        return true;
      }
      if (!offer.closeEscrowDate) {
        return false;
      }
      return daysHaveElapsed(offer.closeEscrowDate, 30);
    default:
      return true;
  }
}

/**
 * Returns the state enum which represents the next step in the progress bar on the customer cards.
 * @param status - ReferralState
 */
function getState(status: ReferralState, offer: IOffer | null): State {
  switch (status) {
    // Nothing more to be done. Progress bar is complete.
    case ReferralState.OFFER_CLOSED:
      return State.COMPLETE;

    // Last remaining step is to close
    case ReferralState.OFFER_CONTINGENCIES_RELEASED:
    case ReferralState.OFFER_IN_CONTRACT:
      return State.CLOSED;

    // Next step is to have your assessment and go into contract
    case ReferralState.OFFER_SENT:
    case ReferralState.OFFER_VIEWED:
    case ReferralState.OFFER_EXPIRING_SOON:
    case ReferralState.OFFER_EXPIRED:
    case ReferralState.OFFER_ACCEPTED:
      // If the scheduled assessments are completed, the next step is to go into contract
      if (offer && assessmentsCompletedForOffer(offer)) {
        return State.CONTRACT;
      }

      return State.ASSESSMENT;

    // Next step is to get an offer, or nothing to be done (denied/withdrawn)
    case ReferralState.LEAD_DENIED:
    case ReferralState.LEAD_QUALIFIED:
    case ReferralState.LEAD_SELLER_FLOW_INCOMPLETE:
    case ReferralState.OFFER_WITHDRAWN:
      return State.OFFER;

    // Default to first step
    default:
      return State.OFFER;
  }
}

function getOfferData(
  lead: NonNullable<IReferral['lead']> | IGetLeadLead,
  state: State,
): Array<{ label: OfferDataLabel; value: string }> {
  const { offer } = lead;
  if (!offer || state === State.OFFER) {
    return [
      {
        label: 'Offer Exp.',
        value: '-',
      },
      {
        label: 'Offer Price',
        value: '-',
      },
      {
        label: 'Net Cash (est.)',
        value: '-',
      },
    ];
  }

  const expiryDate = offer.assessmentScheduled
    ? offer.purchaseAgreementExpiresAt
    : offer.assessmentSchedulingExpiresAt;

  switch (state) {
    case State.ASSESSMENT: {
      // At this stage, the OR/VA is yet to be scheduled
      if (!offer.scheduledOrvaTime) {
        return [
          {
            label: 'Offer Exp.',
            value: expiryDate ? new Date(expiryDate).toLocaleDateString() : '-',
          },
          {
            label: assessmentLabel(false),
            value: '-',
          },
          buildOfferDisplay(offer),
          buildNetCash(offer),
        ];
      }

      let label: OfferDataLabel = assessmentLabel(false);
      let value = new Date(offer.scheduledOrvaTime).toLocaleDateString();

      // If the exterior assessment was scheduled, use this value instead
      if (offer.scheduledEaTime) {
        label = assessmentLabel(true);
        value = new Date(offer.scheduledEaTime).toLocaleDateString();
      }
      return [
        {
          label: 'Offer Exp.',
          value: expiryDate ? new Date(expiryDate).toLocaleDateString() : '-',
        },
        {
          label,
          value,
        },
        buildOfferDisplay(offer),
        buildNetCash(offer),
      ];
    }
    case State.CONTRACT:
      return [
        {
          label: 'Offer Exp.',
          value: expiryDate ? new Date(expiryDate).toLocaleDateString() : '-',
        },
        {
          label: 'Assessments',
          value: 'COMPLETE',
        },
        buildOfferDisplay(offer),
        buildNetCash(offer),
      ];
    case State.CLOSED:
      return [
        {
          label: 'Closing Date',
          value: offer.closeEscrowDate
            ? new Date(`${offer.closeEscrowDate}T00:00:00`).toLocaleDateString()
            : '-',
        },
        {
          label: 'Assessments',
          value: 'COMPLETE',
        },
        buildOfferDisplay(offer),
        buildNetCash(offer),
      ];
    default:
      return [];
  }
}

function buildOfferDisplay(offer: IOffer): { label: OfferDataLabel; value: string } {
  if (
    (offer.underwritingUea?.state === 'completed' || offer.underwritingUea?.state === 'approved') &&
    offer.underwritingUea?.underwritingRecordedPriceCents
  ) {
    return {
      label: 'Final Offer',
      value: centsToCurrencyString(offer?.underwritingUea.underwritingRecordedPriceCents),
    };
  }

  return {
    label: 'Offer Range',
    value: `${centsToCurrencyString(
      offer.sellerVisibleValuationRangeStartCents!,
    )} - ${centsToCurrencyString(offer.sellerVisibleValuationRangeEndCents!)} `,
  };
}

function buildNetCash(offer: IOffer): { label: OfferDataLabel; value: string } {
  if (
    (offer.underwritingUea?.state === 'completed' || offer.underwritingUea?.state === 'approved') &&
    offer.underwritingUea?.estimatedNetProceedsCents
  ) {
    const netCash =
      offer.underwritingUea?.estimatedNetProceedsCents -
      (offer.underwritingUea.underwritingRecordedPriceCents ?? 0) * offer.coBrokePercentage;

    return {
      label: 'Net Cash (est.)',
      value: centsToCurrencyString(netCash),
    };
  }

  return {
    label: 'Net Cash (est.)',
    value: 'Pending Assessment',
  };
}

function getReferralState(lead: IGetLeadLead): ReferralState {
  if (lead.state === 'denied') {
    return ReferralState.LEAD_DENIED;
  }
  const { offer } = lead;
  if (!offer) {
    return ReferralState.UNKNOWN;
  }

  switch (offer.state) {
    case 'expired':
      return ReferralState.OFFER_EXPIRED;
    case 'sent':
      return ReferralState.OFFER_SENT;
    default:
      return ReferralState.UNKNOWN;
  }
}

export function getOfferDetails(data: IGetLead): Offer | null {
  const { lead } = data;
  if (!lead) {
    return null;
  }

  const { flip, offer } = lead;

  let cepName = null;
  if (flip) {
    const acqCxParticipants = flip.participants.filter((participant) => {
      // A participant with both the acq_cx and valuations role is the OVM valuator.
      return (
        participant.roles.indexOf('acq_cx') !== -1 && participant.roles.indexOf('valuations') === -1
      );
    });
    if (acqCxParticipants.length > 0) {
      cepName = acqCxParticipants[0].name;
    }
  }

  const status = getReferralState(lead);
  const state = getState(status, offer);
  const offerData = getOfferData(lead, state);

  return {
    cepName,
    offerData,
    state,
    status,
    url: shouldShowViewOfferUrl(offer) ? `${WEB_API_URL}/offers/${offer!.token}` : '',
    token: offer?.token || null,
  };
}

export function getCustomerCardData(referral: IReferral): CustomerCardData | null {
  const { lead, uuid, sellerInputId } = referral;

  if (!lead) {
    return null;
  }

  const { offer, email, fullName, initialCustomer, address, qualifiedAt, flip } = lead;
  const status = referral.state;
  const state = getState(referral.state, offer);
  const offerData = getOfferData(lead, state);

  let cepName = null;
  if (flip) {
    const acqCxParticipants = flip.participants.filter((participant) => {
      // A participant with both the acq_cx and valuations role is the OVM valuator.
      return (
        participant.roles.indexOf('acq_cx') !== -1 && participant.roles.indexOf('valuations') === -1
      );
    });
    if (acqCxParticipants.length > 0) {
      cepName = acqCxParticipants[0].name;
    }
  }

  return {
    customer: {
      email: initialCustomer?.email ?? email,
      fullName: initialCustomer?.fullName ?? fullName,
      referralUuid: uuid,
      street: address.street,
    },
    offer: {
      cepName,
      offerData,
      state,
      status,
      url: shouldShowViewOfferUrl(offer) ? `${WEB_API_URL}/offers/${offer!.token}` : '',
      token: offer?.token || null,
    },
    hidden: shouldHide(lead, state),
    qualifiedAt: new Date(qualifiedAt!).getTime(),
    sellerInputId,
  };
}

const shouldShowViewOfferUrl = (offer: IOffer | null) => {
  const viewableStates = [
    'draft',
    'ready_to_send',
    'send_in_progress',
    'sent',
    'viewing',
    'expired',
    'requested_to_list',
    'accepted_other',
  ];
  return offer && viewableStates.includes(offer.state);
};

function assessmentsCompletedForOffer(offer: IOffer): boolean {
  return (
    // OR/VA Scheduled?
    !!offer.scheduledOrvaTime &&
    // OR/VA Complete?
    daysHaveElapsed(offer.scheduledOrvaTime, 1) &&
    // EA Scheduled?
    !!offer.scheduledEaTime &&
    // EA Complete?
    daysHaveElapsed(offer.scheduledEaTime, 1)
  );
}

function assessmentLabel(isExteriorAssessment: boolean): OfferDataLabel {
  return isExteriorAssessment ? 'Exterior Asmt.' : 'Interior Asmt.';
}
